/**
 * Classification Backfill API Service (KAN-225)
 *
 * Service for the admin email classification UI — fetches unclassified
 * emails and submits classifications.
 */

import { apiClient } from './client';

export interface BackfillStats {
  total: number;
  classified: number;
  remaining: number;
}

export interface UnclassifiedEmail {
  messageId: string;
  clientId: string;
  clientName: string;
  subject: string;
  sender: string;
  emailDate: string;
  emailType: string;
  classificationLayer: string;
  gmailLabelApplied: string;
  body: string;
  htmlBody: string;
  parsedContent: string;
  attachmentNames: string[];
  bankName: string;
  bankId: string;
  productType: string;
}

export type ClassificationBasis = 'subject' | 'email_body' | 'attachment';

export interface ClassifyPayload {
  message_id: string;
  intent: string;
  basis: ClassificationBasis;
  notes?: string;
}

export const INTENT_OPTIONS = [
  { value: 'confirmation_new', label: 'Confirmation (New)', family: 'confirmation' },
  { value: 'confirmation_amendment', label: 'Same Day Confirmation Amendment', family: 'confirmation' },
  { value: 'trade_modification', label: 'Trade Modification', family: 'confirmation' },
  { value: 'confirmation_unwind', label: 'Confirmation (Unwind)', family: 'confirmation' },
  { value: 'cancellation', label: 'Cancellation', family: 'terminal' },
  { value: 'chaser', label: 'Chaser / Follow-up', family: 'informational' },
  { value: 'acknowledgement', label: 'Acknowledgement', family: 'informational' },
  { value: 'dispute_notice', label: 'Dispute Notice', family: 'informational' },
  { value: 'expiry', label: 'Expiry Notice', family: 'informational' },
  { value: 'settlement_instruction', label: 'Settlement Instruction', family: 'informational' },
  { value: 'fixing_notice', label: 'Fixing Notice', family: 'informational' },
  { value: 'statement', label: 'Statement', family: 'informational' },
  { value: 'mtm_certificate', label: 'MtM Certificate', family: 'informational' },
  { value: 'contract_unsigned', label: 'Contract (For Signing)', family: 'contract' },
  { value: 'contract_signed', label: 'Contract (Signed)', family: 'contract' },
  { value: 'contract_chaser', label: 'Contract Chaser', family: 'contract' },
  { value: 'pre_trade', label: 'Pre-trade / Negotiation', family: 'non_binding' },
  { value: 'maturity_notice', label: 'Maturity Notice', family: 'terminal' },
  { value: 'unknown', label: 'Unknown / Other', family: 'uncertain' },
] as const;

export interface EvalDisagreement {
  evalDocId: string;
  messageId: string;
  subject: string;
  bankName: string;
  humanIntent: string;
  routerIntent: string;
  routerConfidence: number;
  routerRationale: string;
  body: string;
  parsedContent: string;
  attachmentNames: string[];
}

export interface LatestDisagreementsResponse {
  runId: string | null;
  agreementRate: number;
  total: number;
  disagreements: EvalDisagreement[];
}

class ClassificationBackfillService {
  async getStats(): Promise<BackfillStats> {
    const response = await apiClient.get<BackfillStats>('/api/v1/classification-backfill/stats');
    return response.data;
  }

  async getNextUnclassified(): Promise<UnclassifiedEmail | null> {
    const response = await apiClient.get<UnclassifiedEmail | null>('/api/v1/classification-backfill/next');
    return response.data;
  }

  async classify(payload: ClassifyPayload): Promise<void> {
    await apiClient.post('/api/v1/classification-backfill/classify', payload);
  }

  async getLatestDisagreements(): Promise<LatestDisagreementsResponse> {
    const response = await apiClient.get<LatestDisagreementsResponse>(
      '/api/v1/classification-backfill/eval/latest/disagreements'
    );
    return response.data;
  }

  async relabel(messageId: string, newIntent: string): Promise<void> {
    await apiClient.post('/api/v1/classification-backfill/evalset/relabel', {
      message_id: messageId,
      new_intent: newIntent,
    });
  }
}

export const classificationBackfillService = new ClassificationBackfillService();
